import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import tenantService from '../services/tenantService';

const TenantContext = createContext(null);

const fallbackConfigs = {
  default: {
    slug: 'default',
    name: 'PitchLab',
    branding: {
      primaryColor: '#0F172A',
      secondaryColor: '#6366F1',
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
      typography: 'inter',
    },
    features: {
      enableGamification: true,
      enablePhoneSequences: true,
      enableLiveCoaching: false,
      enableObjectionDrills: true,
      enableScripts: true,
      enableMicroLessons: true,
      enableUgc: false,
      enableRoleplay: true,
      enableLeaderboards: true,
      enableConversationAnalyzer: true,
      enableMegaTrainer: true,
    },
  },
  automotive: {
    slug: 'automotive',
    name: 'PitchLab Automotive',
    branding: {
      primaryColor: '#0B1F33',
      secondaryColor: '#E63946',
      backgroundColor: '#F7FAFC',
      textColor: '#0B1F33',
      typography: 'manrope',
    },
    features: {
      enableGamification: false,
      enablePhoneSequences: false,
      enableLiveCoaching: false,
      enableObjectionDrills: true,
      enableScripts: true,
      enableMicroLessons: false,
      enableUgc: false,
      enableRoleplay: true,
      enableLeaderboards: false,
      enableConversationAnalyzer: true,
      enableMegaTrainer: false,
    },
  },
  insurance: {
    slug: 'insurance',
    name: 'PitchLab Insurance',
    branding: {
      primaryColor: '#0A2342',
      secondaryColor: '#2D9CDB',
      backgroundColor: '#F8FBFF',
      textColor: '#0A2342',
      typography: 'manrope',
    },
    features: {
      enableGamification: false,
      enablePhoneSequences: false,
      enableLiveCoaching: false,
      enableObjectionDrills: true,
      enableScripts: true,
      enableMicroLessons: false,
      enableUgc: false,
      enableRoleplay: true,
      enableLeaderboards: false,
      enableConversationAnalyzer: true,
      enableMegaTrainer: false,
    },
  },
};

const resolveTenantSlug = () => {
  if (typeof window === 'undefined') return 'default';

  const params = new URLSearchParams(window.location.search);
  const queryTenant = params.get('tenant');
  if (queryTenant) return queryTenant.toLowerCase();

  const host = window.location.hostname.toLowerCase();
  if (host.includes('automotive')) return 'automotive';
  if (host.includes('insurance')) return 'insurance';
  return 'default';
};

const applyBrandingToCssVars = (branding = {}) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (branding.primaryColor) root.style.setProperty('--tenant-primary', branding.primaryColor);
  if (branding.secondaryColor) root.style.setProperty('--tenant-secondary', branding.secondaryColor);
  if (branding.backgroundColor) root.style.setProperty('--tenant-background', branding.backgroundColor);
  if (branding.textColor) root.style.setProperty('--tenant-text', branding.textColor);
};

export const TenantProvider = ({ children }) => {
  const [tenantSlug, setTenantSlug] = useState(resolveTenantSlug());
  const [tenant, setTenant] = useState(fallbackConfigs[resolveTenantSlug()] || fallbackConfigs.default);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [tenantError, setTenantError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadTenant = async () => {
      setLoadingTenant(true);
      setTenantError(null);

      try {
        const config = await tenantService.getTenantConfigBySlug(tenantSlug);
        if (!isMounted) return;

        if (config) {
          setTenant(config);
          applyBrandingToCssVars(config?.branding);
        } else {
          const fallback = fallbackConfigs[tenantSlug] || fallbackConfigs.default;
          setTenant(fallback);
          applyBrandingToCssVars(fallback?.branding);
        }
      } catch (error) {
        console.warn('Tenant resolution failed; using fallback', error?.message || error);
        const fallback = fallbackConfigs[tenantSlug] || fallbackConfigs.default;
        setTenant(fallback);
        applyBrandingToCssVars(fallback?.branding);
        setTenantError(error);
      } finally {
        if (isMounted) {
          setLoadingTenant(false);
        }
      }
    };

    loadTenant();

    return () => {
      isMounted = false;
    };
  }, [tenantSlug]);

  const value = useMemo(() => ({
    tenantSlug,
    tenantId: tenant?.id || null,
    tenant,
    branding: tenant?.branding,
    features: tenant?.features || {},
    loadingTenant,
    tenantError,
    setTenantSlug,
  }), [tenantSlug, tenant, loadingTenant, tenantError]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
};

export default TenantContext;
